import Commodity from "domain/commodity";
import React, { useCallback } from "react";
import { Dropdown, DropdownButton } from "react-bootstrap";
import { BidTable } from "./BidTable";
import BidTableHeader from "./BidTableHeader";
import BidTableProps from "./bidTableProps";
import usePermission from "common/security/usePermission";
import Permissions from "common/security/permissions";
import classNames from "classnames";
import PhysicalLocation, { AllRegionsHomeLocation, LocationBase, isSelfOrChild } from "domain/physicalLocation";
import DeliveryPeriod, { CommoditySettings, Region } from "domain/deliveryPeriod";
import _ from "lodash";
import { useBooleanUserPreference, useNumberUserPreference } from "features/userPreferences/useUserPreference";
import { Unit } from "domain/unit";
import { localizeBid as localize } from "features/localization/localizeService";
import useColumnDefinitions from "common/agGrid/useColumnDefinitions";
import { ColDef } from "@ag-grid-community/all-modules";
import UnitPicker from "features/settings/commodities/UnitPicker";

const COMMODITY_SELECTION: string = 'Bids_SelectedCommodityID';
const BIDS_COLLAPSE_SELECTION: string = 'Bids_Collapsed';

const MultiCommodityBidTable: React.FC<BidTableProps> = (props) => {
    const showCollapse = usePermission(Permissions.ViewPosition);

    const lastSelectedCommodityId = useNumberUserPreference(COMMODITY_SELECTION);
    const isCollapsed = useBooleanUserPreference(BIDS_COLLAPSE_SELECTION);

    const selectedCommodity = props.commodities.find(c => c.id === lastSelectedCommodityId.value)
        ?? props.commodities[0];
    
    function onRegionSelected(commodity: Commodity, location: LocationBase) {
        const settingsCopy = [...props.selectedCommoditySettings];
        const currentCommodity = settingsCopy.find(x => x.commodityId === commodity.id);
        if(currentCommodity == null) {
            settingsCopy.push(createCommoditySetting(commodity, location));
        }
        else {    
            const regions = currentCommodity.regions; 
            if(!regions.some(l => l.regionId === location.id)) {
                regions.push(createSelectedRegion(commodity, location));
            }
            regions.forEach(rl => rl.isSelected = rl.regionId === location.id)
        }
        props.saveCommoditySettingsChanges(settingsCopy);
    }

    function onUnitSelected(commodity: Commodity, unit: Unit) {
        const settingsCopy = [...props.selectedCommoditySettings];
        const currentCommodity = settingsCopy.find(x => x.commodityId === commodity.id)
        if(currentCommodity == null) {
            settingsCopy.push(createCommoditySetting(commodity, {id: "ALL", name: "All Regions"}, unit));
        }
        else {
            currentCommodity.selectedUnit = unit;
        }
        props.saveCommoditySettingsChanges(settingsCopy);
    }


    function createSelectedRegion(commodity: Commodity, location: LocationBase): Region {
        const region = new Region(location.id, true);
        region.locationIds = 
                props.locations
                .filter(l =>  l.commodities.some(c => c.id === commodity.id) && isSelfOrChild(l, location.id))
                .map(l => l.id);
        return region;
    }

    function createCommoditySetting(commodity: Commodity, location: LocationBase, unit: Unit = null): CommoditySettings {
        const region = createSelectedRegion(commodity, location);
        return new CommoditySettings(commodity.id, [region], unit);
    }

    function getLastSelectedRegion(commodity: Commodity): PhysicalLocation {
        const regions =  props.selectedCommoditySettings.find(x => x.commodityId === commodity.id)?.regions;
        const selectedRegionId = regions?.find(r => r.isSelected).regionId;
        return props.locations.find(l => l.id === selectedRegionId);
    }

    function reorderCommodity(commodityId: number, commodityIndex: number, moveUp: boolean) {

        if ((moveUp && commodityIndex <= 0) || (!moveUp && commodityIndex >= props.commodities.length - 1)) {
            return;
        }

        let lastSelectedCommodityOrder = props.selectedCommodityOrder.length === 0 ? props.commodities.map(x => x.id) : [...props.selectedCommodityOrder ];
        const index = lastSelectedCommodityOrder.indexOf(commodityId);

        /* Check the list of incoming commodities. If they have it filtered, we want to make sure reordering shows correctly
            selectedCommodityOrder will contain all commodities, so we don't want to swap with a commodity that isn't displaying
            For example, selectedCommodityOrder = [1, 2, 3, 4, 5, 6] and commodities = [2, 5, 6]
            If they move ID 5 up, then commodities should be [5, 2, 6] and selectedCommodityOrder should be [1, 5, 2, 3, 4, 6] */

        const swapCommodity = moveUp ? props.commodities[commodityIndex - 1] : props.commodities[commodityIndex + 1];
        
        const indexOfSwap = lastSelectedCommodityOrder.indexOf(swapCommodity.id);
        lastSelectedCommodityOrder.splice(index, 1);
        lastSelectedCommodityOrder.splice(indexOfSwap, 0, commodityId);

        props.saveCommodityOrder(lastSelectedCommodityOrder);
    }

    function getLastSelectedUnit(commodity: Commodity) {
        const selectedUnitId =  props.selectedCommoditySettings.find(x => x.commodityId === commodity.id)?.selectedUnit?.id;
        return commodity.localUnit.id === selectedUnitId ? commodity.localUnit : commodity.baseUnit;
    }
    
    if (props.showCommodityPicker) {
            
        if (selectedCommodity == null) {
            return <h3>No bids</h3>;
        }

        return <TableForCommodity
            commodity={selectedCommodity}
            deliveryPeriods={props.periodsByCommodity[selectedCommodity.id]}
            locations={props.locations}
            selectedCommoditySettings={props.selectedCommoditySettings}
            saveCommoditySettingsChanges={props.saveCommoditySettingsChanges}
            isCustomer={props.isCustomer}
            selectedRegion={getLastSelectedRegion(selectedCommodity) ?? AllRegionsHomeLocation}
            onRegionSelected={l => onRegionSelected(selectedCommodity, l)}
            selectedUnit={getLastSelectedUnit(selectedCommodity)}
            onUnitSelected={u => onUnitSelected(selectedCommodity, u)}
            getColumnDefinitions={() => props.getColumnDefinitions(selectedCommodity, true)}
            hideTable={isCollapsed.value}>
                <>
                    {showCollapse && <div className="collapse-bids col">
                            <i className="material-icons"
                                onClick={() => isCollapsed.update(!isCollapsed.value)}>
                                {isCollapsed.value ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}</i>
                        </div>}  
                    <div className="col d-inline-flex">
                        <DropdownButton className="mr-3" id="commodity-picker" title={localize("MultiCommodityBidTable_Dropdown_SelectedCommodity", {comm: selectedCommodity.displayName})} variant={null}>
                            {props.commodities.map(c =>
                                <Dropdown.Item onClick={() => lastSelectedCommodityId.update(c.id)} key={c.id}>
                                    {c.displayName}
                                </Dropdown.Item>
                            )}
                        </DropdownButton>
                    </div>
                </>
        </TableForCommodity>;
    }
    else {
        return <> {props.commodities.map((c, idx) => 
            <TableForCommodity key={c.id} 
                commodity={c}
                deliveryPeriods={props.periodsByCommodity[c.id]}
                locations={props.locations}
                selectedCommoditySettings={props.selectedCommoditySettings}
                saveCommoditySettingsChanges={props.saveCommoditySettingsChanges}
                isCustomer={props.isCustomer}
                selectedRegion={getLastSelectedRegion(c) ?? AllRegionsHomeLocation}
                onRegionSelected={l => onRegionSelected(c, l)}
                selectedUnit={getLastSelectedUnit(c)}
                onUnitSelected={u => onUnitSelected(c, u)}
                getColumnDefinitions={() => props.getColumnDefinitions(c, true)}>
                    <div className={classNames("collapse-bids col", { disabled: idx === 0 })}>
                        <i className="material-icons" onClick={() => reorderCommodity(c.id, idx, true)}>
                            keyboard_arrow_up
                        </i>
                    </div>
                    <div className="col-3">
                        <span>{c.displayName}</span>
                        <div className={classNames("collapse-bids col", { disabled: idx === props.commodities.length - 1 })}>
                            <i className="material-icons" onClick={() => reorderCommodity(c.id, idx, false)}>
                                keyboard_arrow_down
                            </i>
                        </div>
                    </div>
            </TableForCommodity>)}
        </>
    }
}

interface TableForCommodityProps {
    commodity: Commodity;
    deliveryPeriods: DeliveryPeriod[];
    locations: PhysicalLocation[];
    selectedCommoditySettings: CommoditySettings[];
    saveCommoditySettingsChanges(commoditySettings: CommoditySettings[]): any;
    isCustomer: boolean;
    selectedRegion: LocationBase;
    onRegionSelected(region: LocationBase): void;
    selectedUnit: Unit;
    onUnitSelected(unit: Unit): void;
    getColumnDefinitions(): ColDef[];

    hideTable?: boolean;
}
const TableForCommodity: React.FC<TableForCommodityProps> = React.memo(props => {

    const columns = useColumnDefinitions(() => props.getColumnDefinitions(), [props.selectedRegion, props.selectedUnit, props.getColumnDefinitions]);

    return <div className="container-fluid">
        <BidTableHeader commodity={props.commodity}
            locations = {props.locations}
            selectedCommoditySettings={props.selectedCommoditySettings}
            saveCommoditySettingsChanges={props.saveCommoditySettingsChanges}
            isCustomer={props.isCustomer}>
            <div className="row">
                {props.children}
                
                <RegionPicker
                    options={props.locations.filter(l => l.homeLocation == null)}
                    selectedRegion={props.selectedRegion}
                    onRegionSelected={props.onRegionSelected} />

                {props.commodity?.baseUnit.id !== props.commodity?.localUnit.id &&
                    <UnitPicker className="ml-3 mr-3"
                        options={[props.commodity.baseUnit, props.commodity.localUnit]}
                        selectedUnit={props.selectedUnit}
                        onUnitSelected={props.onUnitSelected} />
                }

                <div className="col">
                    {localize("MultiCommodityBidTable_Label_CurrencyCodeListed", {currencyCode: props.commodity.localCurrency.code})}
                </div>
                
            </div>
        </BidTableHeader>
        <div className={classNames("row", {'collapsed-bid-table': props.hideTable})}>
            {props.hideTable !== true && 
                <BidTable
                    deliveryPeriods={props.deliveryPeriods}
                    columnDefinitions={columns}
                    />
            }

        </div>
    </div>
});

interface RegionPickerProps {
    options: LocationBase[];
    selectedRegion: LocationBase;
    onRegionSelected(region: LocationBase): void;
}

const RegionPicker: React.FC<RegionPickerProps> = React.memo(props => {
    return <DropdownButton id="region-picker" className="ml-3 mr-3"
        title={props.selectedRegion.id == 'ALL' ? localize("MultiCommodityBidTable_Dropdown_AllRegions") : props.selectedRegion.name} variant={null}>
        <Dropdown.Item onClick={() => props.onRegionSelected(AllRegionsHomeLocation)} key={AllRegionsHomeLocation.id}>
            {localize("MultiCommodityBidTable_Dropdown_AllRegions")}
        </Dropdown.Item>
        {props.options.map(l =>
            <Dropdown.Item onClick={() => props.onRegionSelected(l)} key={l.id}>
                {l.name}
            </Dropdown.Item>
        )}
    </DropdownButton>;
});

export default MultiCommodityBidTable